"use client";

import { useCallback, useMemo, useState } from "react";
import { EventCard } from "@/components/events/EventCard";
import { pickEventsHeroImage } from "@/lib/events-map";
import { CalendarX } from "lucide-react";
import { useRouter, useSearchParams } from "next/navigation";
import type { Event, HomeSectionCopy } from "@/lib/api-types";
import { Button } from "@/components/ui/Button";
import { Dialog } from "@/components/ui/Dialog";
import { EmptyState } from "@/components/ui/EmptyState";
import { WaveDivider } from "@/components/decorative/WaveDivider";
import { PageHero } from "@/components/layout/PageHero";
import { SitePageShell } from "@/components/layout/SitePageShell";
import { Reveal, Stagger, StaggerItem } from "@/components/motion";
import { assets } from "@/content/assets";
import { DEFAULT_EVENTS_SECTION } from "@/lib/site-content-fallbacks";
import { cn } from "@/lib/utils";

type EventFilter = "all" | "upcoming" | "completed";

interface EventsListProps {
  events: Event[];
  section?: HomeSectionCopy;
}

function parseFilter(value: string | null): EventFilter {
  if (value === "upcoming" || value === "completed") return value;
  return "all";
}

export function EventsList({
  events,
  section = DEFAULT_EVENTS_SECTION,
}: EventsListProps) {
  const router = useRouter();
  const searchParams = useSearchParams();
  const [selected, setSelected] = useState<Event | null>(null);

  const filter = useMemo(
    () => parseFilter(searchParams.get("filter")),
    [searchParams]
  );

  const setFilter = useCallback(
    (next: EventFilter) => {
      const params = new URLSearchParams(searchParams.toString());
      if (next === "all") {
        params.delete("filter");
      } else {
        params.set("filter", next);
      }
      const query = params.toString();
      router.replace(query ? `/events?${query}` : "/events", { scroll: false });
    },
    [router, searchParams]
  );

  const filtered = events.filter((e) => {
    const status = e.status.toLowerCase();
    if (filter === "all") return true;
    return status === filter;
  });

  const openEvent = (event: Event) => {
    setSelected(event);
  };

  const closeEvent = () => {
    setSelected(null);
  };

  const heroImage = pickEventsHeroImage(events) ?? assets.eventsHero;

  // #region agent log
  fetch("http://127.0.0.1:7342/ingest/670e42c9-685b-4027-9b65-6227ab74989a", {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      "X-Debug-Session-Id": "9b3fb2",
    },
    body: JSON.stringify({
      sessionId: "9b3fb2",
      runId: "verify-events-section",
      hypothesisId: "C",
      location: "EventsList.tsx:render",
      message: "EventsList PageHero section prop",
      data: {
        eyebrow: section.eyebrow,
        title: section.title,
        subtitle: section.subtitle,
        matchesDefault:
          section.eyebrow === DEFAULT_EVENTS_SECTION.eyebrow &&
          section.title === DEFAULT_EVENTS_SECTION.title,
      },
      timestamp: Date.now(),
    }),
  }).catch(() => {});
  // #endregion

  return (
    <SitePageShell className="pb-16">
      <PageHero
        eyebrow={section.eyebrow}
        title={section.title}
        subtitle={section.subtitle}
        layout="split"
        align="left"
        image={{
          src: heroImage,
          alt: "Community members participating in a foundation activity",
          imageClassName: "object-[center_35%]",
        }}
      >
        <Stagger className="flex flex-wrap gap-2">
          {(["all", "upcoming", "completed"] as const).map((f) => (
            <StaggerItem key={f}>
              <button
                type="button"
                onClick={() => setFilter(f)}
                className={cn(
                  "site-filter-pill capitalize",
                  filter === f
                    ? "site-filter-pill-active"
                    : "site-filter-pill-inactive"
                )}
              >
                {f}
              </button>
            </StaggerItem>
          ))}
        </Stagger>
      </PageHero>

      <WaveDivider palette="site" className="text-[var(--site-bg)]" />

      <div className="relative mx-auto max-w-7xl px-4 md:px-6">
        <h2 className="sr-only">Event listings</h2>
        {filtered.length === 0 ? (
          <Reveal>
            <EmptyState
              icon={CalendarX}
              title="No events to show"
              description="Check back soon for upcoming community drives and activities."
              action={{ href: "/contact", label: "Contact us" }}
            />
          </Reveal>
        ) : (
          <Stagger className="grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
            {filtered.map((event) => (
              <StaggerItem key={event.id}>
                <button
                  type="button"
                  onClick={() => openEvent(event)}
                  aria-label={`View details for ${event.title}`}
                  className="group h-full w-full text-left"
                >
                  <EventCard event={event} interactive />
                </button>
              </StaggerItem>
            ))}
          </Stagger>
        )}

        <div
          aria-hidden
          className="pointer-events-none absolute inset-x-0 bottom-0 h-24 bg-gradient-to-b from-transparent to-[var(--site-bg)] opacity-60"
        />
      </div>

      <Dialog
        open={selected !== null}
        onClose={closeEvent}
        title={selected?.title ?? ""}
        showCloseButton={false}
      >
        {selected && (
          <>
            <p className="mb-4 text-sm text-[var(--site-accent)]">{selected.date}</p>
            <p className="mb-4 text-[var(--site-muted)]">{selected.description}</p>
            {selected.items && (
              <p className="text-sm text-[var(--site-text)]">
                <strong>Impact:</strong> {selected.items}
              </p>
            )}
            <Button
              type="button"
              variant="ghost"
              size="sm"
              className="mt-4"
              onClick={closeEvent}
            >
              Close
            </Button>
          </>
        )}
      </Dialog>
    </SitePageShell>
  );
}
